import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import * as moment from 'moment';
import * as _ from 'lodash';
import {InitializationAndConfigurationService} from './configuration/initializationAndConfiguration.service';
import {UserService} from './user.service';
import {UrlService} from './url.service';

export interface VisibilityGrantSearchCriteria {
    firstName: string;
    lastName: string;
    identifierTypeCode?: string;
    identifier?: string;
    dob?: any;
    lastSsn?: string;
}

@Injectable()
export class ClientService {
    private BASE_URL: string;

    constructor(private http: HttpClient,
                private userService: UserService,
                private initializationAndConfigurationService: InitializationAndConfigurationService,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/clients';
    }

    getClient(clientId): any {
        return this.http.get(this.BASE_URL + '/' + clientId);
    }

    getClientServiceUsesByOrgUnit(clientId: string, startDate: string, endDate: string): Observable<any> {
        const params = new HttpParams()
            .set('startDate', startDate)
            .set('endDate', endDate);
        return this.http.get(this.BASE_URL + '/' + clientId + '/clientServiceUses/byOrgUnit', {params});
    }

    getAllClients(params: HttpParams): any {
        return this.http.get(this.BASE_URL, {params: params});
    }

    getAllClientsWorkload(params: HttpParams): Observable<any> {
        return this.http.get(this.BASE_URL + '/workload', {params: params});
    }

    getCaregivers(clientId, start, limit, sortField, sortOrder, filter) {
        let params = new HttpParams();
        if (start) {
            params = params.set('start', start);
        }
        if (limit) {
            params = params.set('limit', limit);
        }
        if (sortField) {
            params = params.set('sortField', sortField);
        }
        if (sortOrder) {
            params = params.set('sortOrder', sortOrder);
        }
        if (filter) {
            params = params.set('filter', JSON.stringify(filter));
        }

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clientId + '/caregivers', requestOptions);
    }

    createCaregiver(caregiver, clientId) {
        return this.http.post(this.BASE_URL + '/' + clientId + '/caregivers', caregiver);
    }

    updateCaregiver(caregiver, clientId) {
        return this.http.put(this.BASE_URL + '/' + clientId + '/caregivers/' + caregiver.id, caregiver);
    }

    addToWorkload(clients: any[]) {
        return this.http.post(this.BASE_URL + '/workload', clients);
    }

    removeFromWorkload(clients: any[]) {
        let params: HttpParams = new HttpParams();
        const clientsIds = [];
        clients.forEach((client) => {
            clientsIds.push(client.id);
        });

        // Send one parameter per entry.  The endpoint binds these to a List<Long>, which Jersey
        // populates from repeated parameters -- it does not split a single comma-separated value.
        clientsIds.forEach((clientId) => {
            params = params.append('clientsIds', clientId);
        });

        const requestOptions = {
            params: params
        };

        return this.http.delete(this.BASE_URL + '/workload', requestOptions);
    }

    getClientPlacement(clientId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/placements');
    }

    getClientPlacementSingle(clientId, clientPlacementId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/placements/' + clientPlacementId);
    }

    getTicklerTracksForPlacement(clientId, placementId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/placements/' + placementId + '/ticklerTracks');
    }

    closeTicklerTrack(clientId, placementId, trackId) {
        return this.http.post(this.BASE_URL + '/' + clientId + '/placements/' + placementId + '/ticklerTracks/' + trackId + '/close', {});
    }

    getCreatableTrackTypes(clientId, placementId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/placements/' + placementId + '/ticklerTracks/creatableTypes');
    }

    createTicklerTrack(clientId, placementId, identifier, startDate) {
        return this.http.post(this.BASE_URL + '/' + clientId + '/placements/' + placementId + '/ticklerTracks',
            {identifier: identifier, startDate: startDate});
    }

    getActiveInstrumentTypes(client, baseTypes) {
        let params = new HttpParams();

        // Send one parameter per entry.  The endpoint binds these to a List<String>, which Jersey
        // populates from repeated parameters -- it does not split a single comma-separated value.
        (baseTypes || []).forEach((baseType) => {
            params = params.append('baseTypes', baseType);
        });

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + client.id + '/activeInstruments', requestOptions);
    }

    getChronoline(clientId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/chronoline');
    }

    isCansOu(ouTags) {
        if (this.initializationAndConfigurationService.getApplicationConfiguration().cansOuTags === 'null') {
            return true;
        }
        let ret = false;
        _.every(ouTags, (tagCode) => {
            if (this.initializationAndConfigurationService.getApplicationConfiguration().cansOuTags.indexOf(tagCode) !== -1) {
                ret = true;
                return false; // Jump out of every()
            }
            return true;
        });
        return ret;
    }

    getVisibleOus(client, assessmentsCreationGraceDays, alwaysIncludeOus, cansOusOnly, instrumentType) {
        const ret = [];
        const usedPlacements = [];

        let cutoffDate = null;

        if (assessmentsCreationGraceDays != null
            && assessmentsCreationGraceDays !== -1) {
            cutoffDate = moment().subtract(assessmentsCreationGraceDays, 'day');
        }

        client.placements.forEach((placement) => {
            let useThisEndDate = null;
            if (placement.endDate != null && placement.adjustedEndDate != null) {
                useThisEndDate = placement.endDate > placement.adjustedEndDate ? placement.endDate : placement.adjustedEndDate;
            } else if (placement.endDate != null) {
                useThisEndDate = placement.endDate;
            } else {
                useThisEndDate = placement.adjustedEndDate;
            }

            if (useThisEndDate == null ||
                (cutoffDate != null && moment(useThisEndDate) >= cutoffDate) ||
                (placement.temporaryOpenDate != null && (moment(placement.temporaryOpenDate).add(3, 'days') >= moment()))) {
                if (this.userService.userIsOuResponsible(placement.orgUnit, this.initializationAndConfigurationService.getLoggedInUser())
                    || this.userService.userIsClientResponsible(client, this.initializationAndConfigurationService.getLoggedInUser())) {
                    if (usedPlacements[placement.orgUnit.code] == null) {
                        if (!cansOusOnly || this.isCansOu(placement.orgUnit.tagsCodes)) {

                            let add = true;
                            if (this.initializationAndConfigurationService.getApplicationConfiguration().instrumentTypeClientOUFilter !== 'null' && instrumentType != null) {
                                const instrumentTypeClientOUFilterMap = {};
                                _.each(this.initializationAndConfigurationService.getApplicationConfiguration()
                                    .instrumentTypeClientOUFilter.split('|'), (filter) => {
                                    const filterComponents = filter.split(':');
                                    instrumentTypeClientOUFilterMap[filterComponents[0]] = filterComponents[1] + ',' + filterComponents[2];
                                });

                                add = false;
                                const ouList = instrumentTypeClientOUFilterMap[instrumentType];

                                if (!ouList) {
                                    add = true;
                                }

                                if (add === false) {
                                    const filterList = ouList.split(',');

                                    if (filterList[0] === 'Include') {
                                        if (_.find(filterList, function (obj) {
                                            return obj === placement.orgUnit.code
                                        })) {
                                            add = true;
                                        }
                                    } else if (filterList[0] === 'Exclude') {
                                        if (_.find(filterList, function (obj) {
                                            return obj === placement.orgUnit.code
                                        })) {
                                            add = false;
                                        } else {
                                            add = true;
                                        }
                                    } else {
                                        // problem in the configuration
                                    }
                                }
                            }

                            if (add === true) {
                                ret.push(placement.orgUnit);
                                usedPlacements[placement.orgUnit.code] = placement;
                            }
                        }
                    }
                }
            }
        });

        // alwaysIncludeOus are probably cases where the assessment might already have an OU
        // coded, but that OU placement is closed.  In that case, if editing an Approved for
        // a closed placement that OU might be passed in, just so an empty list isn't returned.
        if (alwaysIncludeOus) {
            alwaysIncludeOus.forEach((ou) => {
                if (ou && usedPlacements[ou.code] == null) {
                    ret.push(ou);
                }
            });
        }

        return ret;
    }

    /** @param identifierTypeCode optional -- omit to search the installation's primary identifier type. */
    searchAllByIdentifierDobSsn(identifier: string, dob: any, lastSsn: string,
                                identifierTypeCode?: string): Observable<any> {
        let params = new HttpParams();
        if (identifier) {
            params = params.set('identifier', identifier);
        }
        if (dob) {
            params = params.set('dob', moment(dob).format('YYYY-MM-DD'));
        }
        if (lastSsn) {
            params = params.set('lastFourSsn', lastSsn);
        }
        if (identifierTypeCode) {
            params = params.set('identifierTypeCode', identifierTypeCode);
        }

        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/searchAllByIdentifierDobSsn', requestOptions);
    }

    claimClientAccess(clientId: number, organizationId: number): Observable<any> {
        // const params = {
        //     clientId: clientId,
        //     organizationId: organizationId
        // };
        const params = new HttpParams()
            .set('clientId', clientId.toString())
            .set('organizationId', organizationId.toString());
        return this.http.post(this.BASE_URL + '/claimClientAccess', {}, { params });
    }

    getClientIdentifierTypes(): Observable<any> {
        return this.http.get(this.BASE_URL + '/identifierTypes');
    }

    searchForOnboarding(firstName: string, lastName: string, dob: any,
                        identifierValue: string, identifierTypeCode: string, lastFourSsn: string): Observable<any> {
        let params = new HttpParams();
        if (firstName) {
            params = params.set('firstName', firstName);
        }
        if (lastName) {
            params = params.set('lastName', lastName);
        }
        if (dob) {
            params = params.set('dob', moment(dob).format('YYYY-MM-DD'));
        }
        if (identifierValue) {
            params = params.set('identifierValue', identifierValue);
        }
        if (identifierTypeCode) {
            params = params.set('identifierTypeCode', identifierTypeCode);
        }
        if (lastFourSsn) {
            params = params.set('lastFourSsn', lastFourSsn);
        }
        return this.http.get(this.BASE_URL + '/searchForOnboarding', { params });
    }

    addClientIdentifier(clientId: number, identifierTypeCode: string, identifierValue: string): Observable<any> {
        const params = new HttpParams()
            .set('clientId', clientId.toString())
            .set('identifierTypeCode', identifierTypeCode)
            .set('identifierValue', identifierValue);
        return this.http.post(this.BASE_URL + '/addClientIdentifier', {}, { params });
    }

    /** @param responsibleUserId optional -- the server defaults the placement to the logged-in user. */
    createClientPlacement(clientId: number, organizationId: number,
                          responsibleUserId?: number): Observable<any> {
        let params = new HttpParams()
            .set('clientId', clientId.toString())
            .set('organizationId', organizationId.toString());
        if (responsibleUserId) {
            params = params.set('responsibleUserId', responsibleUserId.toString());
        }
        return this.http.post(this.BASE_URL + '/claimClientAccess', {}, { params });
    }

    /**
     * @param gender optional -- a Gender enum name; omitted leaves the client's gender unset.
     * @param responsibleUserId optional -- the server defaults the placement to the logged-in user.
     */
    onboardClient(firstName: string, lastName: string, dob: string,
                  identifierTypeCode: string, identifierValue: string, organizationId: number,
                  gender?: string, responsibleUserId?: number): Observable<any> {
        let params = new HttpParams()
            .set('firstName', firstName)
            .set('lastName', lastName)
            .set('dob', dob)
            .set('identifierTypeCode', identifierTypeCode)
            .set('identifierValue', identifierValue)
            .set('organizationId', organizationId.toString());
        if (gender) {
            params = params.set('gender', gender);
        }
        if (responsibleUserId) {
            params = params.set('responsibleUserId', responsibleUserId.toString());
        }
        return this.http.post(this.BASE_URL + '/onboardClient', {}, { params });
    }

    /** Exact first and last name plus at least one of identifier, DOB or last 4 of SSN. Audited server-side. */
    searchForVisibilityGrant(criteria: VisibilityGrantSearchCriteria): Observable<any> {
        return this.http.get(this.BASE_URL + '/searchForVisibilityGrant',
            { params: this.visibilityGrantSearchParams(criteria) });
    }

    /**
     * Sends the search inputs, not a client id: the server re-runs the search and grants only a single match, so a
     * grant can't be made for a client the user hasn't identified. Formats the inputs exactly as
     * searchForVisibilityGrant does, so the server sees the same search that produced the displayed client.
     */
    grantClientVisibility(criteria: VisibilityGrantSearchCriteria,
                          expirationDate: string, reason: string): Observable<any> {
        let params = this.visibilityGrantSearchParams(criteria);
        if (expirationDate) {
            params = params.set('expirationDate', expirationDate);
        }
        if (reason) {
            params = params.set('reason', reason);
        }
        return this.http.post(this.BASE_URL + '/grantClientVisibility', {}, { params });
    }

    private visibilityGrantSearchParams(criteria: VisibilityGrantSearchCriteria): HttpParams {
        let params = new HttpParams()
            .set('firstName', criteria.firstName)
            .set('lastName', criteria.lastName);
        if (criteria.identifier) {
            params = params.set('identifier', criteria.identifier);
            // The type only means something alongside an identifier.
            if (criteria.identifierTypeCode) {
                params = params.set('identifierTypeCode', criteria.identifierTypeCode);
            }
        }
        if (criteria.dob) {
            params = params.set('dob', moment(criteria.dob).format('YYYY-MM-DD'));
        }
        if (criteria.lastSsn) {
            params = params.set('lastFourSsn', criteria.lastSsn);
        }
        return params;
    }

    /** Everyone's grants for a grant manager; otherwise only the caller's own. Active, expired and revoked. */
    getClientVisibilityGrants(clientId): Observable<any> {
        return this.http.get(this.BASE_URL + '/' + clientId + '/visibilityGrants');
    }

    revokeClientVisibilityGrant(grantId: number): Observable<any> {
        const params = new HttpParams().set('grantId', grantId.toString());
        return this.http.post(this.BASE_URL + '/revokeVisibilityGrant', {}, { params });
    }
}
